import React from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { Components, Theme } from '@mui/material/styles';

/**
 * Create OutlinedInput Component Theme
 * Most commonly used input variant
 */
export const createOutlinedInputTheme = (
  theme: Theme,
): Components['MuiOutlinedInput'] => {
  return {
    styleOverrides: {
      root: {
        borderRadius: '6px !important',
        '&:not(.MuiInputBase-multiline)': {
          height: '40px',
        },
        // Readonly state - set background to secondary
        '& .MuiInputBase-input[readonly]': {
          backgroundColor: theme.palette.background.secondary,
          cursor: 'default',
        },
      },
    },
  };
};

/**
 * Create TextField Component Theme
 * Wrapper component that uses InputBase and OutlinedInput
 */
export const createTextFieldTheme = (
  theme: Theme,
): Components['MuiTextField'] => {
  return {
    defaultProps: {
      size: 'small',
    },
  };
};

/**
 * Create FormHelperText Component Theme
 * Used for helper text and error messages
 */
export const createFormHelperTextTheme = (
  theme: Theme,
): Components['MuiFormHelperText'] => {
  return {
    styleOverrides: {
      root: {
        marginTop: `${theme.spacing(0.5)} !important`,
        marginLeft: '0',
        marginRight: '0',
      },
    },
  };
};

/**
 * Create Select Component Theme
 * Based on Portal Pixels selector design
 */
export const createSelectTheme = (theme: Theme): Components['MuiSelect'] => {
  return {
    defaultProps: {
      IconComponent: ExpandMoreIcon,
    },
    styleOverrides: {
      root: {
        height: '40px',
        borderRadius: '6px !important',
        '& .MuiSelect-icon': {
          color: theme.palette.text.primary,
          fontSize: '16px',
          right: '12px',
        },
      },
    },
  };
};

/**
 * Create Autocomplete Component Theme
 * Uses ExpandMore icon for dropdown indicator, consistent with Select
 */
export const createAutocompleteTheme = (
  theme: Theme,
): Components['MuiAutocomplete'] => {
  return {
    defaultProps: {
      popupIcon: React.createElement(ExpandMoreIcon),
    },
    styleOverrides: {
      root: {
        '& .MuiAutocomplete-popupIndicator': {
          color: theme.palette.text.primary,
        },
      },
    },
  };
};

/**
 * Create MenuItem Component Theme
 * Used in Select dropdowns
 */
export const createMenuItemTheme = (
  theme: Theme,
): Components['MuiMenuItem'] => {
  return {
    styleOverrides: {
      root: {
        padding: theme.spacing(1, 1.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
          backgroundColor: theme.palette.background.secondary,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.secondary.light,
          '&:hover': {
            backgroundColor: theme.palette.secondary.light,
          },
        },
        '&.Mui-disabled': {
          color: theme.palette.text.buttonTextDisabled,
        },
      },
    },
  };
};

/**
 * Create Menu Component Theme
 * Used for Select dropdown menu container
 */
export const createMenuTheme = (theme: Theme): Components['MuiMenu'] => {
  return {
    styleOverrides: {
      paper: {
        borderRadius: 6,
        backgroundColor: theme.palette.background.primary,
        boxShadow: `0 4px 16px -2px ${theme.palette.grey[500]}`,
      },
    },
  };
};

export const createCheckboxTheme = (
  theme: Theme,
): Components['MuiCheckbox'] => {
  return {
    defaultProps: {
      icon: React.createElement(CheckBoxOutlineBlankIcon),
      checkedIcon: React.createElement(CheckBoxIcon),
      indeterminateIcon: React.createElement(IndeterminateCheckBoxIcon),
    },
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
        padding: theme.spacing(1),
        '& .MuiSvgIcon-root': {
          fontSize: 20,
          borderRadius: '6px !important',
        },
        '&.Mui-disabled': {
          color: theme.palette.text.disabled,
          '& .MuiSvgIcon-root': {
            backgroundColor: theme.palette.text.disabled,
            borderRadius: '6px',
          },
          '&.Mui-checked': {
            color: theme.palette.text.disabled,
            opacity: 0.6,
            '& .MuiSvgIcon-root': {
              backgroundColor: theme.palette.background.primary,
            },
          },
          '&.Mui-indeterminate': {
            color: theme.palette.text.disabled,
            opacity: 0.6,
            '& .MuiSvgIcon-root': {
              backgroundColor: theme.palette.background.primary,
            },
          },
        },
      },
    },
  };
};

export const createSwitchTheme = (theme: Theme): Components['MuiSwitch'] => {
  return {
    styleOverrides: {
      root: {
        width: 50,
        height: 26,
        padding: 0,
        marginLeft: theme.spacing(1),
        '& .MuiSwitch-switchBase': {
          padding: 3,
          '&.Mui-checked': {
            transform: 'translateX(24px)',
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: theme.palette.background.default,
            },
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.text.disabled,
                opacity: 0.4,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: theme.palette.background.tertiary,
                opacity: 0.8,
              },
            },
          },
          '&.Mui-disabled': {
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.text.disabled,
              opacity: 0.4,
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: theme.palette.background.tertiary,
              opacity: 0.8,
            },
          },
        },
        '& .MuiSwitch-thumb': {
          width: 20,
          height: 20,
          backgroundColor: theme.palette.background.primary,
          boxShadow: 'none',
        },
        '& .MuiSwitch-track': {
          borderRadius: 30,
          backgroundColor: theme.palette.text.disabled,
          opacity: 1,
        },
      },
    },
  };
};

export default createTextFieldTheme;
